import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { Box, Inline } from '@atlaskit/primitives';
import { ActionName, SmartLinkDirection, SmartLinkSize, SmartLinkWidth } from '../../../../../../constants';
import ActionGroup from '../../action-group';
import Block from '../../block';
import ElementGroup from '../../element-group';
import AIStateIndicator from '../ai-state-indicator';
import { renderElementItems } from '../../utils';
import { messages } from '../../../../../../messages';
import { useAISummary } from '../../../../../../state/hooks/use-ai-summary';
import { useFlexibleUiContext } from '../../../../../../state/flexible-ui-context';
import AIIcon from '../../../../../common/ai-icon';
import AISummary from '../../../../../common/ai-summary';
import { useAnalyticsEvents } from '../../../../../../common/analytics/generated/use-analytics-events';
import AIEventSummaryViewed from '../ai-event-summary-viewed';
import AIEventErrorViewed from '../ai-event-error-viewed';
var AISummaryBlockResolvedView = function AISummaryBlockResolvedView(props) {
  var _props$actions = props.actions,
    actions = _props$actions === void 0 ? [] : _props$actions,
    metadata = props.metadata,
    onActionMenuOpenChange = props.onActionMenuOpenChange,
    _props$size = props.size,
    size = _props$size === void 0 ? SmartLinkSize.Medium : _props$size,
    testId = props.testId,
    aiSummaryMinHeight = props.aiSummaryMinHeight;
  var metadataElements = renderElementItems(metadata);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    fireEvent = _useAnalyticsEvents.fireEvent;
  var context = useFlexibleUiContext();
  var url = (context === null || context === void 0 ? void 0 : context.url) || '';
  var ari = (context === null || context === void 0 ? void 0 : context.ari) || '';
  var _useAISummary = useAISummary({
      url: url,
      ari: ari
    }),
    summariseUrl = _useAISummary.summariseUrl,
    _useAISummary$state = _useAISummary.state,
    content = _useAISummary$state.content,
    status = _useAISummary$state.status,
    error = _useAISummary$state.error;
  var showAISummary = status === 'done' ||
  // We want to display the AI Summary component only when there is content available during the loading process.
  status === 'loading' && !!content;
  var isSummarisedOnMountRef = useRef(status === 'done');
  var isErroredOnMountRef = useRef(status === 'error');
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showAISummaryErrorMessage = _useState2[0],
    setShowAISummaryErrorMessage = _useState2[1];
  useEffect(function () {
    // if the error was apparent on mount and the status is changed to loading we can
    // clear the initial error on mount state
    if (isErroredOnMountRef.current && status === 'loading') {
      isErroredOnMountRef.current = false;
    } else {
      // if a new error occurs and it was not an apparent error on mount, we can show the error message
      setShowAISummaryErrorMessage(!isErroredOnMountRef.current && status === 'error');
    }
  }, [status]);
  var combinedActions = useMemo(function () {
    if (status === 'ready' || status === 'error') {
      var aiAction = {
        content: /*#__PURE__*/React.createElement(FormattedMessage, messages.ai_summarize),
        name: ActionName.CustomAction,
        onClick: function onClick() {
          fireEvent('ui.button.clicked.aiSummary', {});
          summariseUrl();
        },
        testId: "".concat(testId, "-ai-summary-action"),
        icon: /*#__PURE__*/React.createElement(AIIcon, {
          label: "AIIcon"
        })
      };
      return [aiAction].concat(_toConsumableArray(actions));
    }
    return actions;
  }, [actions, status, testId, summariseUrl, fireEvent]);
  var onDropdownOpenChange = useCallback(function (isOpen) {
    if (onActionMenuOpenChange) {
      onActionMenuOpenChange({
        isOpen: isOpen
      });
    }
  }, [onActionMenuOpenChange]);
  return /*#__PURE__*/React.createElement(Block, _extends({}, props, {
    direction: SmartLinkDirection.Vertical,
    testId: "".concat(testId, "-resolved-view")
  }), status === 'done' && /*#__PURE__*/React.createElement(AIEventSummaryViewed, {
    fromCache: isSummarisedOnMountRef.current
  }), showAISummary && /*#__PURE__*/React.createElement(AISummary, {
    testId: "".concat(testId, "-ai-summary"),
    minHeight: aiSummaryMinHeight,
    content: content,
    showIcon: isSummarisedOnMountRef.current
  }), /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    alignInline: "end",
    grow: "fill",
    spread: "space-between"
  }, /*#__PURE__*/React.createElement(Box, null, !isSummarisedOnMountRef.current && (status === 'loading' || status === 'done') ? /*#__PURE__*/React.createElement(AIStateIndicator, {
    state: status,
    testId: "".concat(testId, "-state-indicator")
  }) : /*#__PURE__*/React.createElement(ElementGroup, {
    width: SmartLinkWidth.FitToContent,
    testId: "".concat(testId, "-metadata-group")
  }, metadataElements)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ActionGroup, {
    onDropdownOpenChange: onDropdownOpenChange,
    items: combinedActions,
    appearance: "default",
    size: size
  }))), showAISummaryErrorMessage && /*#__PURE__*/React.createElement(Inline, {
    grow: "fill"
  }, /*#__PURE__*/React.createElement(AIEventErrorViewed, {
    reason: error
  }), /*#__PURE__*/React.createElement(AIStateIndicator, {
    error: error,
    state: status,
    testId: testId
  })));
};
export default AISummaryBlockResolvedView;